import type {
  IndianMarketCharges,
  PlanExecutionData,
  PlanExecutionReport,
  PlanPurchase,
  PlanSell,
  PlanTimelineEvent,
} from '../models/PlanExecutionTypes';
import { getPlanPurchases } from '../models/PlanExecutionTypes';

const LTCG_HOLDING_DAYS = 365;
const STCG_RATE = 0.15;
const LTCG_RATE = 0.1;
const LTCG_EXEMPTION = 100_000;

export function grossAmount(quantity: number, pricePerShare: number): number {
  return quantity * pricePerShare;
}

export function sumCharges(charges: IndianMarketCharges): number {
  return (
    charges.stt +
    charges.brokerage +
    charges.gstOnBrokerage +
    charges.exchangeTxnCharges +
    charges.sebiTurnoverFees +
    charges.stampDuty
  );
}

export function mergeCharges(a: IndianMarketCharges, b: IndianMarketCharges): IndianMarketCharges {
  return {
    stt: a.stt + b.stt,
    brokerage: a.brokerage + b.brokerage,
    gstOnBrokerage: a.gstOnBrokerage + b.gstOnBrokerage,
    exchangeTxnCharges: a.exchangeTxnCharges + b.exchangeTxnCharges,
    sebiTurnoverFees: a.sebiTurnoverFees + b.sebiTurnoverFees,
    stampDuty: a.stampDuty + b.stampDuty,
  };
}

export function daysBetween(from: string, to: string): number {
  const start = new Date(from);
  const end = new Date(to);
  return Math.max(0, Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
}

interface PurchaseLot {
  purchase: PlanPurchase;
  remainingQty: number;
}

function buildLots(purchases: PlanPurchase[]): PurchaseLot[] {
  return [...purchases]
    .sort((a, b) => a.tradeDate.localeCompare(b.tradeDate))
    .map((p) => ({ purchase: p, remainingQty: p.quantity }));
}

function sellGainFifo(purchases: PlanPurchase[], sell: PlanSell): number {
  const lots = buildLots(purchases);
  let qtyLeft = sell.quantity;
  const sellChargesPerShare = sumCharges(sell.charges) / sell.quantity;
  let gain = 0;
  while (qtyLeft > 0.0001 && lots.length > 0) {
    const lot = lots[0];
    const take = Math.min(qtyLeft, lot.remainingQty);
    const buyChargesPerShare = sumCharges(lot.purchase.charges) / lot.purchase.quantity;
    const costBasis = lot.purchase.pricePerShare + buyChargesPerShare;
    const netProceeds = sell.pricePerShare - sellChargesPerShare;
    gain += (netProceeds - costBasis) * take;
    lot.remainingQty -= take;
    qtyLeft -= take;
    if (lot.remainingQty <= 0.0001) lots.shift();
  }
  return gain;
}

export function capitalGainsTypeForSell(purchases: PlanPurchase[], sell: PlanSell): 'STCG' | 'LTCG' {
  const lots = buildLots(purchases);
  let qtyLeft = sell.quantity;
  let anyStcg = false;
  while (qtyLeft > 0.0001 && lots.length > 0) {
    const lot = lots[0];
    const take = Math.min(qtyLeft, lot.remainingQty);
    if (daysBetween(lot.purchase.tradeDate, sell.tradeDate) < LTCG_HOLDING_DAYS) {
      anyStcg = true;
    }
    lot.remainingQty -= take;
    qtyLeft -= take;
    if (lot.remainingQty <= 0.0001) lots.shift();
  }
  return anyStcg ? 'STCG' : 'LTCG';
}

/** @deprecated Use capitalGainsTypeForSell(purchases, sell) */
export function capitalGainsTypeForSellSingle(purchase: PlanPurchase, sell: PlanSell): 'STCG' | 'LTCG' {
  return capitalGainsTypeForSell([purchase], sell);
}

export function suggestBuyCharges(gross: number): IndianMarketCharges {
  const brokerage = Math.min(gross * 0.0003, 20);
  return {
    stt: gross * 0.001,
    brokerage,
    gstOnBrokerage: brokerage * 0.18,
    exchangeTxnCharges: gross * 0.0000325,
    sebiTurnoverFees: gross * 0.000001,
    stampDuty: gross * 0.00015,
  };
}

export function suggestSellCharges(gross: number): IndianMarketCharges {
  const brokerage = Math.min(gross * 0.0003, 20);
  return {
    stt: gross * 0.001,
    brokerage,
    gstOnBrokerage: brokerage * 0.18,
    exchangeTxnCharges: gross * 0.0000325,
    sebiTurnoverFees: gross * 0.000001,
    stampDuty: 0,
  };
}

export function buildExecutionReport(data: PlanExecutionData): PlanExecutionReport | null {
  const purchases = getPlanPurchases(data);
  const { sells } = data;
  if (purchases.length === 0) return null;

  const sharesBought = purchases.reduce((s, p) => s + p.quantity, 0);
  const totalPurchaseValue = purchases.reduce(
    (s, p) => s + grossAmount(p.quantity, p.pricePerShare),
    0
  );
  const avgPurchasePrice = sharesBought > 0 ? totalPurchaseValue / sharesBought : 0;

  const sharesSold = sells.reduce((s, x) => s + x.quantity, 0);
  const totalSellProceeds = sells.reduce((s, x) => s + grossAmount(x.quantity, x.pricePerShare), 0);
  const avgSellPrice = sharesSold > 0 ? totalSellProceeds / sharesSold : 0;

  let totalCharges = { ...emptyCharges() };
  for (const p of purchases) {
    totalCharges = mergeCharges(totalCharges, p.charges);
  }
  for (const sell of sells) {
    totalCharges = mergeCharges(totalCharges, sell.charges);
  }

  let stcgGain = 0;
  let ltcgGain = 0;
  for (const sell of sells) {
    const gain = sellGainFifo(purchases, sell);
    if (sell.capitalGainsType === 'LTCG') ltcgGain += gain;
    else stcgGain += gain;
  }

  const estimatedStcgTax = stcgGain > 0 ? stcgGain * STCG_RATE : 0;
  const taxableLtcg = Math.max(0, ltcgGain - LTCG_EXEMPTION);
  const estimatedLtcgTax = taxableLtcg > 0 ? taxableLtcg * LTCG_RATE : 0;
  const realizedGainBeforeTax = stcgGain + ltcgGain;
  const netRealizedGain = realizedGainBeforeTax - estimatedStcgTax - estimatedLtcgTax;

  return {
    sharesBought,
    avgPurchasePrice,
    totalPurchaseValue,
    sharesSold,
    avgSellPrice,
    totalSellProceeds,
    sharesRemaining: sharesBought - sharesSold,
    totalCharges,
    realizedGainBeforeTax,
    estimatedStcgTax,
    estimatedLtcgTax,
    netRealizedGain,
  };
}

function emptyCharges(): IndianMarketCharges {
  return {
    stt: 0,
    brokerage: 0,
    gstOnBrokerage: 0,
    exchangeTxnCharges: 0,
    sebiTurnoverFees: 0,
    stampDuty: 0,
  };
}

export function buildTimelineEvents(
  data: PlanExecutionData,
  noteEvents: { id: string; date: string; body: string }[] = []
): PlanTimelineEvent[] {
  const events: PlanTimelineEvent[] = [];
  const purchases = getPlanPurchases(data);

  purchases.forEach((p, idx) => {
    const gross = grossAmount(p.quantity, p.pricePerShare);
    events.push({
      id: p.id ?? `purchase-${p.tradeDate}-${idx}`,
      type: 'PURCHASE',
      date: p.tradeDate,
      title: purchases.length > 1 ? `Stock purchase #${idx + 1}` : 'Stock purchase',
      subtitle: `${p.quantity} shares @ ₹${p.pricePerShare.toFixed(2)}`,
      body: p.comments,
      metrics: [
        { label: 'Gross', value: `₹${gross.toLocaleString('en-IN')}` },
        { label: 'Charges', value: `₹${sumCharges(p.charges).toLocaleString('en-IN')}` },
      ],
    });
  });

  for (const sell of data.sells) {
    const gross = grossAmount(sell.quantity, sell.pricePerShare);
    events.push({
      id: sell.id,
      type: 'SELL',
      date: sell.tradeDate,
      title: `Partial sell (${sell.capitalGainsType})`,
      subtitle: `${sell.quantity} shares @ ₹${sell.pricePerShare.toFixed(2)}`,
      body: sell.comments,
      metrics: [
        { label: 'Proceeds', value: `₹${gross.toLocaleString('en-IN')}` },
        { label: 'Charges', value: `₹${sumCharges(sell.charges).toLocaleString('en-IN')}` },
      ],
    });
  }

  for (const note of noteEvents) {
    events.push({
      id: note.id,
      type: 'NOTE',
      date: note.date,
      title: 'Timeline note',
      body: note.body,
    });
  }

  return events.sort((a, b) => b.date.localeCompare(a.date) || b.id.localeCompare(a.id));
}

export function purchaseTotalCost(p: PlanPurchase): number {
  return grossAmount(p.quantity, p.pricePerShare) + sumCharges(p.charges);
}
